import React from 'react';
import { Card, Table, Typography, Tag, Tooltip } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

export const CampaignOrderQueueCampaignsSummary = ({ campaigns, form, currentCampaignOrder }) => {
  // Show for APPROVED, LIVE_APPROVED, LIVE, PAUSED statuses
  // OR when any campaign is in a launching state
  const isValidOrderStatus = ['APPROVED', 'LIVE_APPROVED', 'LIVE', 'PAUSED'].includes(currentCampaignOrder?.status);
  const hasLaunchingCampaign = campaigns?.some(campaign =>
    ['LAUNCHING_LIVE', 'LAUNCHING_PAUSED'].includes(campaign?.status)
  );

  if (!isValidOrderStatus && !hasLaunchingCampaign) {
    return null;
  }

  const columns = [
    {
      title: 'Campaign Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, _record, index) => (
        <Text strong>{text || `Campaign ${index + 1}`}</Text>
      ),
    },
    {
      title: 'Pricing Model',
      dataIndex: 'pricingModel',
      key: 'pricingModel',
      render: (pricingModel) => {
        if (!pricingModel) {
          return <Tag icon={<CloseCircleOutlined />} color="default">Not Set</Tag>;
        }

        if (pricingModel === 'Percentage') {
          return (
            <Tooltip title="Budget-based pricing where costs are calculated as a percentage of the budget allocation">
              <Tag color="purple">Percentage</Tag>
            </Tooltip>
          );
        }

        if (pricingModel === 'CPM') {
          return (
            <Tooltip title="Impression-based pricing (Cost Per Mille) where costs are based on impressions delivered">
              <Tag color="blue">CPM</Tag>
            </Tooltip>
          );
        }

        return <Tag color="default">{pricingModel}</Tag>;
      },
    },
    {
      title: 'DSP',
      dataIndex: 'dsp',
      key: 'dsp',
      render: (text) => {
        if (!text) {
          return <Tag icon={<CloseCircleOutlined />} color="default">Not Set</Tag>;
        }
        return <Tag icon={<CheckCircleOutlined />} color="green">{text}</Tag>;
      },
    },
    {
      title: 'Ad Provider ID',
      dataIndex: 'adProviderId',
      key: 'adProviderId',
      render: (text) => {
        if (!text) {
          return <Tag icon={<CloseCircleOutlined />} color="default">Not Set</Tag>;
        }
        return <Text>{text}</Text>;
      },
    },
    {
      title: 'Maximum Bid',
      dataIndex: 'maxBid',
      key: 'maxBid',
      render: (value) => {
        if (value === null || value === undefined || value === 0) {
          return <Tag icon={<CloseCircleOutlined />} color="default">Not Set</Tag>;
        }
        return <Text>${Number(value).toFixed(2)}</Text>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        // Campaign status color mapping
        const statusConfig = {
          PENDING: { color: 'default', text: 'Pending' },
          APPROVED: { color: 'blue', text: 'Approved' },
          LAUNCHING: { color: 'processing', text: 'Launching' },
          LAUNCHING_LIVE: { color: 'processing', text: 'Launching Live' },
          LAUNCHING_PAUSED: { color: 'processing', text: 'Launching Paused' },
          LIVE: { color: 'success', text: 'Live' },
          PAUSED: { color: 'warning', text: 'Paused' },
          COMPLETED: { color: 'default', text: 'Completed' },
          ARCHIVED: { color: 'default', text: 'Archived' },
          ERROR: { color: 'error', text: 'Error' },
        };

        const config = statusConfig[status] || { color: 'default', text: status || 'Not Set' };

        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: 'Launch Type',
      dataIndex: 'isLaunchProgrammatically',
      key: 'isLaunchProgrammatically',
      render: (isLaunchProgrammatically, record) => {
        // Show launch type if campaign is LIVE, PAUSED, or LAUNCHING
        if (!['LIVE', 'PAUSED', 'LAUNCHING_LIVE', 'LAUNCHING_PAUSED'].includes(record.status)) {
          return <Text type="secondary">-</Text>;
        }

        if (isLaunchProgrammatically) {
          return <Tag color="purple">Programmatic</Tag>;
        }
        return <Tag color="blue">Manual</Tag>;
      },
    },
  ];

  // Get form values to show current state
  const formCampaigns = form.getFieldValue('campaigns') || [];

  // Get pricing model from campaign order (priority order: campaignOrder.pricingModel -> advertiser.pricingModel.model)
  const pricingModel = currentCampaignOrder?.pricingModel || currentCampaignOrder?.advertiser?.pricingModel?.model || null;

  const dataSource = campaigns.map((campaign, index) => {
    const formValues = formCampaigns[index] || {};
    return {
      key: campaign.id || index,
      name: campaign.name,
      pricingModel: pricingModel,  // Pricing model from campaign order
      dsp: formValues.demandSidePlatform || campaign.demandSidePlatform,
      adProviderId: formValues.adProviderId || campaign.adProviderId,
      maxBid: formValues.maxBid !== undefined ? formValues.maxBid : campaign.maxBid,
      status: campaign.status,  // Campaign status from the database
      isLaunchProgrammatically: campaign.isLaunchProgrammatically,  // Launch type from the database
    };
  });

  return (
    <Card style={{ marginBottom: 24 }}>
      <Title level={5} style={{ marginBottom: 16 }}>CAMPAIGNS SUMMARY</Title>
      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        size="small"
        bordered
      />
    </Card>
  );
};
